import _ from 'lodash';
import React, {FC, PropsWithChildren, useCallback, useState} from 'react';

import {useConstant} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  WorkspaceDisplayActionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../../../libs/shared/core/src/models/tracking/contexts/workspaceTracking';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {InboxesPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useNavigationBlocking} from '../../../../../../srcCommon/app/navigationBlocking';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {CollapsedItemStates, CollapsedStateKey} from '../../../../../helpers/expansionTogglingHelpers';
import {findEditModeParentTags} from '../../../../../helpers/models/tagHelpers';
import {
  buildCustomViewExpandedKey,
  computeParentTagOverridesMap,
  ParentTagOverride,
  ParentTagOverridesEnum,
} from '../../../../../helpers/visualCustomViewHelpers';
import {LensModel} from '../../../../../models/api/lensModel';
import {SearchTagModel} from '../../../../../models/api/searchTagModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {VisualWorkspaceModel} from '../../../../../models/api/visualWorkspaceModel';
import {WorkspaceFolderRootLabelsEnum} from '../../../../../models/api/workspaceModel';
import {ThunkAction} from '../../../../../store/appThunk';
import {dataUpdatedAction} from '../../../../../store/data/dataGroupsActions';
import {updateWorkspacePreferencesAction} from '../../../../../store/data/preferences/preferencesActions';
import {updateParentTagAction} from '../../../../../store/data/tags/saveTagActions';
import {getTags} from '../../../../../store/data/tags/tagsSelectors';
import {
  WorkspaceDropNestTagArgs,
  WorkspaceDropNestTagTargetRelationshipsEnum,
  WorkspaceDropReorderLensHandler,
  WorkspaceDropReorderTagHandler,
  WorkspaceDropReorderTeamInboxHandler,
  WorkspaceDropReorderTeammateHandler,
  WorkspaceDropReorderTeammateWorkspaceItemHandler,
  WorkspaceDropReorderTeamViewHandler,
  WorkspaceTagDropNestHandler,
} from '../../../../../store/data/workspace/reorder/types';
import {
  isSameVisualWorkspace,
  LensModelMap,
  TagModelMap,
  TeamInboxModelMap,
  TeammateModelMap,
} from '../../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {
  reorderVisualWorkspaceLens,
  reorderVisualWorkspaceNestedFolder,
  reorderVisualWorkspaceTeamInbox,
  reorderVisualWorkspaceTeammate,
  reorderVisualWorkspaceTeammateWorkspaceTag,
  reorderVisualWorkspaceTeamView,
  showVisualWorkspaceLens,
  toggleVisualWorkspaceLens,
  toggleVisualWorkspaceRootFolder,
  toggleVisualWorkspaceTeamCustomView,
  toggleVisualWorkspaceTeamInbox,
  toggleVisualWorkspaceTeammate,
  WorkspaceChangeId,
  WorkspaceTeamChange,
} from '../../../../../store/data/workspace/visualWorkspace/visualWorkspaceUpdateHelpers';
import {updateVisualWorkspaceAsyncAction} from '../../../../../store/data/workspace/workspaceActions';
import {stoppedEditingWorkspacesAction} from '../../../../../store/panels/inboxes/inboxesPanelActions';
import {trackSaveSidebarEvent} from './trackingHelpers';
import {ToggleExpandItemArgs, ToggleExpandTagArgs, WorkspacesEditorDialogsEnum} from './types';
import {ToggleTagParams, WorkspacesEditorContext} from './workspacesEditorContext';

/*
 * Props.
 */

interface WorkspacesEditorProviderProps {
  panelId: InboxesPanelId;
  visualWorkspace: VisualWorkspaceModel;
}

/*
 * Component.
 */

export const WorkspacesEditorProvider: FC<PropsWithChildren<WorkspacesEditorProviderProps>> = (props) => {
  const {children, panelId, visualWorkspace} = props;

  const [visibleDialog, setVisibleDialog] = useState<WorkspacesEditorDialogsEnum | null>(null);

  const showDiscardChangesDialog = () => setVisibleDialog(WorkspacesEditorDialogsEnum.DISCARD);
  const showConflictDialog = () => setVisibleDialog(WorkspacesEditorDialogsEnum.CONFLICT);
  const showErrorDialog = () => setVisibleDialog(WorkspacesEditorDialogsEnum.ERROR);

  // Handlers to control whether or not to reload tag results. The purpose of reloading is to display a
  // newly created tag.
  const [shouldReloadTagResults, setShouldReloadTagResults] = useState(false);
  const onReloadTagResults = () => setShouldReloadTagResults(true);
  const onReloadTagResultsDone = () => setShouldReloadTagResults(false);

  const dispatch = useAppDispatch();

  // Take snapshot of workspace when we start edit mode.
  const initialWorkspace = useConstant(() => visualWorkspace);

  const [draftWorkspace, setDraftWorkspace] = useState<VisualWorkspaceModel>(initialWorkspace);

  const [isSavingWorkspaces, setIsSavingWorkspaces] = useState<boolean>(false);

  const [parentTagOverrides, setParentTagOverrides] = useState<ReadonlyArray<ParentTagOverride>>([]);

  const parentTagOverridesMap = computeParentTagOverridesMap(parentTagOverrides);

  const hasChangedParentTag =
    Object.values(parentTagOverridesMap).filter((value) => value !== ParentTagOverridesEnum.NO_CHANGE)
      .length > 0;

  // Stores the newly created tags in P1 edit mode.
  const [createdTags, setCreatedTags] = useState<Array<TagModel>>([]);

  // If the user has created any tag, the 'save' button will be activated.
  const hasCreatedAnyTag = createdTags.length > 0;

  const hasAnyChanges =
    hasChangedParentTag || hasCreatedAnyTag || !isSameVisualWorkspace(initialWorkspace, draftWorkspace);

  const [collapsedItems, setCollapsedItems] = useState<CollapsedItemStates>({});

  // Stores all the tags we've loaded in the tag search modals.
  // This ensures that we can find the TagModel objects even when they're not in the redux store.
  const [tagModelMap, setTagModelMap] = useState<TagModelMap>({});

  // Stores all the team inboxes we've toggled in the team inbox search modals.
  const [teamInboxModelMap, setTeamInboxModelMap] = useState<TeamInboxModelMap>({});

  // Stores all the team custom views we've toggled in the team custom view search modals.
  // Note that custom views are represented by TagModel objects in the dropdown.
  const [teamCustomViewModelMap, setTeamCustomViewModelMap] = useState<TagModelMap>({});

  // Stores all the teammates we've toggled in the teammates search modals.
  const [teammateModelMap, setTeammateModelMap] = useState<TeammateModelMap>({});

  // Stores all the lenses we've toggled in the lenses search modals.
  const [lensModelMap, setLensModelMap] = useState<LensModelMap>({});

  const {didBlockNavigation, unblockNavigation, cancelNavigation} = useNavigationBlocking(panelId, true);

  /**
   * Based on nestSingleTagInsideNewParentAction, which is used when nesting by drag-and-drop outside of edit mode.
   * @see nestSingleTagInsideNewParentAction
   */
  const setParentTagOverrideAction =
    ({childTagId, targetTagId, targetRelationship}: WorkspaceDropNestTagArgs): ThunkAction =>
    (_dispatch, getState) => {
      const tagsFromState = getTags(getState());
      const findTag = (id: TagModel['id']): TagModel | undefined => tagsFromState[id] ?? tagModelMap[id];

      const childTag = findTag(childTagId);
      if (!childTag) {
        throw new Error('The tag to nest could not be found.');
      }

      const targetTag = (targetTagId && findTag(targetTagId)) || undefined;

      const newParentTag =
        targetRelationship === WorkspaceDropNestTagTargetRelationshipsEnum.SIBLING && targetTag?.parentTagId
          ? tagsFromState[targetTag.parentTagId]
          : targetTag;

      const parentTagIdOverride =
        newParentTag?.id === childTag.parentTagId
          ? ParentTagOverridesEnum.NO_CHANGE
          : newParentTag?.id ?? ParentTagOverridesEnum.MOVED_TO_ROOT;

      setParentTagOverrides((prevArray) => {
        // If the same child tag gets re-nested consecutively, only keep the last instance of those consecutive renesting actions.
        // Note: this array can probably be further optimized but the purpose is to maintain the order of actions and therefore
        // prevent tag cycles that will cause the app to crash.
        if (prevArray.length > 0 && prevArray[prevArray.length - 1].childTagId === childTagId) {
          return [...prevArray.slice(0, -1), {childTagId, newParentTagId: parentTagIdOverride}];
        }
        return [...prevArray, {childTagId, newParentTagId: parentTagIdOverride}];
      });
    };

  /**
   * Note: event tracking is already done within WorkspaceCustomViewItemContainer for both edit and regular mode.
   * @see WorkspaceCustomViewItemContainer
   */
  const onDropNestTag: WorkspaceTagDropNestHandler = (dropArgs) => {
    const {childTagId, targetTagId, customView} = dropArgs;
    const childTag = draftWorkspace.teammateWorkspaceTags.find(({id}) => id === childTagId);
    const targetTag = draftWorkspace.teammateWorkspaceTags.find(({id}) => id === targetTagId);
    if (childTag && targetTag && customView && childTag.isSelected && !targetTag.isSelected) {
      // Handle the specific case when selected tags are nested under de-selected tags. In this case, we have to
      // auto-select the de-selected parent tags. This includes the targetTag and all the parentTags.
      const {tag} = customView;
      const parentTagIds = findEditModeParentTags(tagModelMap, parentTagOverridesMap, tag).map(({id}) => id);
      setDraftWorkspace((prevWorkspace) => ({
        ...prevWorkspace,
        teammateWorkspaceTags: prevWorkspace.teammateWorkspaceTags.map((userTag) => {
          if (!parentTagIds.includes(userTag.id) && !(userTag.id === targetTag.id)) {
            return userTag;
          }
          return {
            ...userTag,
            isSelected: true,
          };
        }),
      }));
    }

    dispatch(setParentTagOverrideAction(dropArgs));
  };

  const onDropReorderTag: WorkspaceDropReorderTagHandler = (dropArgs) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
      section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
      if_auto_suggested: false,
    });
    setDraftWorkspace((previousDraftWorkspace) =>
      reorderVisualWorkspaceTeammateWorkspaceTag(previousDraftWorkspace, dropArgs),
    );
  };

  const onDropReorderNestedFolder: WorkspaceDropReorderTeammateWorkspaceItemHandler = (dropArgs) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
      section: WorkspaceDisplaySectionTrackingTypesEnum.INDIV_SUB_INBOX,
      if_auto_suggested: false,
    });
    setDraftWorkspace((previousDraftWorkspace) =>
      reorderVisualWorkspaceNestedFolder(previousDraftWorkspace, dropArgs),
    );
  };

  const onDropReorderTeamInbox: WorkspaceDropReorderTeamInboxHandler = (dropArgs) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
      section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
      if_auto_suggested: false,
    });
    setDraftWorkspace((previousDraftWorkspace) =>
      reorderVisualWorkspaceTeamInbox(previousDraftWorkspace, dropArgs),
    );
  };

  const onDropReorderTeamView: WorkspaceDropReorderTeamViewHandler = (dropArgs) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
      section: WorkspaceDisplaySectionTrackingTypesEnum.CUSTOM_VIEW,
      if_auto_suggested: false,
    });
    setDraftWorkspace((previousDraftWorkspace) =>
      reorderVisualWorkspaceTeamView(previousDraftWorkspace, dropArgs),
    );
  };

  const onDropReorderTeammate: WorkspaceDropReorderTeammateHandler = (dropArgs) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
      section: WorkspaceDisplaySectionTrackingTypesEnum.TEAMMATES,
      if_auto_suggested: false,
    });
    setDraftWorkspace((previousDraftWorkspace) =>
      reorderVisualWorkspaceTeammate(previousDraftWorkspace, dropArgs),
    );
  };

  const onDropReorderLens: WorkspaceDropReorderLensHandler = (dropArgs) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
      section: WorkspaceDisplaySectionTrackingTypesEnum.LENS,
      if_auto_suggested: false,
    });
    setDraftWorkspace((previousDraftWorkspace) =>
      reorderVisualWorkspaceLens(previousDraftWorkspace, dropArgs),
    );
  };

  // For toggling a teammate within P1.
  const onToggleTeammate = (id: WorkspaceChangeId, ifUsedHelpTip: boolean) => {
    setDraftWorkspace((previousDraftWorkspace) => {
      const {updatedVisualWorkspace, trackingAction, isRecentlyViewed} = toggleVisualWorkspaceTeammate(
        previousDraftWorkspace,
        id,
      );
      if (trackingAction) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: trackingAction,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TEAMMATES,
          if_auto_suggested: isRecentlyViewed === false,
          if_used_help_tip: ifUsedHelpTip,
        });
      }
      return updatedVisualWorkspace;
    });
  };

  // For toggling a teammate in a search modal.
  const onToggleDropdownTeammate = (teammate: TeammateModel) => {
    const {id} = teammate;
    setDraftWorkspace((prevWorkspace) => {
      const isShowingTeammate = !prevWorkspace.teammates.items.map((t) => t.id).includes(id);
      if (isShowingTeammate) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: WorkspaceDisplayActionsEnum.ADD,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TEAMMATES,
        });
      }

      return {
        ...prevWorkspace,
        teammates: {
          // If we are pinning a teammate who wasn't shown before make sure the workspace is visible.
          isHidden: isShowingTeammate ? false : visualWorkspace.teammates.isHidden,
          items: isShowingTeammate
            ? prevWorkspace.teammates.items.concat({isSelected: true, id, isRecentlyViewed: undefined})
            : prevWorkspace.teammates.items.filter((t) => t.id !== id),
        },
      };
    });
    onAddTeammateToMap(teammate);
  };

  // For toggling a lens within P1.
  const onToggleLens = (id: WorkspaceChangeId, ifUsedHelpTip: boolean) => {
    setDraftWorkspace((previousDraftWorkspace) => {
      const {updatedVisualWorkspace, trackingAction, isRecentlyViewed} = toggleVisualWorkspaceLens(
        previousDraftWorkspace,
        id,
      );
      if (trackingAction) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: trackingAction,
          section: WorkspaceDisplaySectionTrackingTypesEnum.LENS,
          if_auto_suggested: isRecentlyViewed === false,
          if_used_help_tip: ifUsedHelpTip,
        });
      }

      return updatedVisualWorkspace;
    });
  };

  // For toggling a lens in a search modal.
  const onToggleDropdownLens = (lens: LensModel) => {
    const {id} = lens;
    setDraftWorkspace((prevWorkspace) => {
      const isShowingLens = !prevWorkspace.lenses.items.map((l) => l.id).includes(id);
      if (isShowingLens) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: WorkspaceDisplayActionsEnum.ADD,
          section: WorkspaceDisplaySectionTrackingTypesEnum.LENS,
        });
      }

      return {
        ...prevWorkspace,
        lenses: {
          // If we are pinning a lens who wasn't shown before make sure the workspace is visible.
          isHidden: isShowingLens ? false : visualWorkspace.lenses.isHidden,
          items: isShowingLens
            ? prevWorkspace.lenses.items.concat({isSelected: true, id, isRecentlyViewed: undefined})
            : prevWorkspace.lenses.items.filter((l) => l.id !== id),
        },
      };
    });
    onAddLensToMap(lens);
  };

  const onAddLensToMap = (lens: LensModel) => {
    setLensModelMap((prevMap) => {
      if (lens.id in prevMap) {
        return prevMap;
      }
      return {
        ...prevMap,
        [lens.id]: lens,
      };
    });
    dispatch(dataUpdatedAction({lenses: [lens]}));
  };

  // For adding a newly created lens to P1.
  const onCreateLens = (id: WorkspaceChangeId) => {
    setDraftWorkspace((previousDraftWorkspace) => {
      const {updatedVisualWorkspace, trackingAction} = showVisualWorkspaceLens(previousDraftWorkspace, id);
      if (trackingAction) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: trackingAction,
          section: WorkspaceDisplaySectionTrackingTypesEnum.LENS,
          if_auto_suggested: false,
        });
      }
      return updatedVisualWorkspace;
    });
  };

  const onAddTeammateToMap = (teammate: TeammateModel) => {
    setTeammateModelMap((prevMap) => {
      if (teammate.id in prevMap) {
        return prevMap;
      }
      return {
        ...prevMap,
        [teammate.id]: teammate,
      };
    });
  };

  // For toggling a team inbox within P1.
  const onToggleTeamInbox = (teamChange: WorkspaceTeamChange, ifUsedHelpTip: boolean) => {
    setDraftWorkspace((previousDraftWorkspace) => {
      const {updatedVisualWorkspace, trackingAction, isRecentlyViewed} = toggleVisualWorkspaceTeamInbox(
        previousDraftWorkspace,
        teamChange,
      );
      if (trackingAction) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: trackingAction,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
          if_auto_suggested: isRecentlyViewed === false,
          if_used_help_tip: ifUsedHelpTip,
        });
      }
      return updatedVisualWorkspace;
    });
  };

  // For toggling a team inbox in a search modal.
  const onToggleDropdownTeamInbox = (inbox: InboxModel, teamId: number) => {
    const {id} = inbox;
    setDraftWorkspace((prevWorkspace) => ({
      ...prevWorkspace,
      teams: prevWorkspace.teams.map((teamData) => {
        if (teamData.teamId !== teamId) {
          return teamData;
        }

        const isHidingInbox = teamData.inboxes.map((selectedInbox) => selectedInbox.id).includes(id);
        if (!isHidingInbox) {
          trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
            action: WorkspaceDisplayActionsEnum.ADD,
            section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
          });
        }

        return {
          ...teamData,
          inboxes: isHidingInbox
            ? teamData.inboxes.filter((selectedInbox) => selectedInbox.id !== id)
            : teamData.inboxes.concat({id, isSelected: true, isRecentlyViewed: undefined}),
        };
      }),
    }));
    onAddTeamInboxToMap(inbox);
  };

  const onAddTeamInboxToMap = (inbox: InboxModel) => {
    setTeamInboxModelMap((prevMap) => {
      if (inbox.id in prevMap) {
        return prevMap;
      }
      return {
        ...prevMap,
        [inbox.id]: inbox,
      };
    });
  };

  // For toggling a team view within P1.
  const onToggleTeamCustomView = (teamChange: WorkspaceTeamChange, ifUsedHelpTip: boolean) => {
    setDraftWorkspace((previousDraftWorkspace) => {
      const {updatedVisualWorkspace, trackingAction, isRecentlyViewed} = toggleVisualWorkspaceTeamCustomView(
        previousDraftWorkspace,
        teamChange,
      );
      if (trackingAction) {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: trackingAction,
          section: WorkspaceDisplaySectionTrackingTypesEnum.CUSTOM_VIEW,
          if_auto_suggested: isRecentlyViewed === false,
          if_used_help_tip: ifUsedHelpTip,
        });
      }
      return updatedVisualWorkspace;
    });
  };

  // For toggling a team view in a dropdown.
  const onToggleDropdownTeamCustomView = (customView: TagModel, teamId: number) => {
    const {id} = customView;
    setDraftWorkspace((prevWorkspace) => ({
      ...prevWorkspace,
      teams: prevWorkspace.teams.map((teamData) => {
        if (teamData.teamId !== teamId) {
          return teamData;
        }

        const isHidingCustomView = teamData.customViews
          .map((selectedCustomView) => selectedCustomView.id)
          .includes(id);

        if (!isHidingCustomView) {
          trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
            action: WorkspaceDisplayActionsEnum.ADD,
            section: WorkspaceDisplaySectionTrackingTypesEnum.CUSTOM_VIEW,
          });
        }

        return {
          ...teamData,
          customViews: isHidingCustomView
            ? teamData.customViews.filter((selectedCustomView) => selectedCustomView.id !== id)
            : teamData.customViews.concat({id, isSelected: true, isRecentlyViewed: undefined}),
        };
      }),
    }));
    onAddTeamCustomViewToMap(customView);
  };

  const onAddTeamCustomViewToMap = (customView: TagModel) => {
    setTeamCustomViewModelMap((prevMap) => {
      if (customView.id in prevMap) {
        return prevMap;
      }
      return {
        ...prevMap,
        [customView.id]: customView,
      };
    });
  };

  const onToggleRootFolder = (id: WorkspaceFolderRootLabelsEnum) => {
    setDraftWorkspace((previousDraftWorkspace) => {
      const {updatedVisualWorkspace, trackingAction} = toggleVisualWorkspaceRootFolder(
        previousDraftWorkspace,
        id,
      );
      trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
        action: trackingAction,
        section: id,
      });
      return updatedVisualWorkspace;
    });
  };

  // For toggling a tag within P1.
  const onToggleTag = (params: ToggleTagParams) => {
    const {tag, allParentTags, allChildTags, ifUsedHelpTip} = params;

    setDraftWorkspace((prevWorkspace) => ({
      ...prevWorkspace,
      teammateWorkspaceTags: prevWorkspace.teammateWorkspaceTags.map((userTag) => {
        // we are de-selecting so also de-select its children
        if (userTag.isSelected) {
          if (userTag.id !== tag.id && !allChildTags.map((t) => t.id).includes(userTag.id)) {
            return userTag;
          }

          trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
            action: WorkspaceDisplayActionsEnum.HIDE,
            section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
            if_auto_suggested: userTag.isRecentlyViewed === false,
            if_used_help_tip: ifUsedHelpTip,
          });
          const childTag = allChildTags.find(({id}) => id === userTag.id);
          if (childTag) {
            onAddTagToMap(childTag);
          }

          return {
            ...userTag,
            isSelected: !userTag.isSelected,
          };
        }
        // we are selecting so we also select its parents
        if (userTag.id !== tag.id && !allParentTags.map((t) => t.id).includes(userTag.id)) {
          return userTag;
        }

        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: WorkspaceDisplayActionsEnum.DISPLAY,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
        });
        const parentTag = allParentTags.find(({id}) => id === userTag.id);
        if (parentTag) {
          onAddTagToMap(parentTag);
        }

        return {
          ...userTag,
          isSelected: !userTag.isSelected,
        };
      }),
    }));
    onAddTagToMap(tag);
  };

  // For tags created in edit mode.
  const onTagCreated = (tag: TagModel, parentTags: ReadonlyArray<TagModel>) => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
      action: WorkspaceDisplayActionsEnum.ADD,
      section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
    });

    setDraftWorkspace((prevWorkspace) => ({
      ...prevWorkspace,
      teammateWorkspaceTags: prevWorkspace.teammateWorkspaceTags.concat({
        id: tag.id,
        isSelected: true,
        isRecentlyViewed: undefined,
      }),
    }));
    onAddTagToMap(tag);
    setCreatedTags((prevCreatedTags) => prevCreatedTags.concat(tag));
    updateParentsOfCreatedTag(parentTags);
  };

  // Show the parent tags of a newly created tag in workspace if necessary.
  const updateParentsOfCreatedTag = (parentTags: ReadonlyArray<TagModel>) => {
    if (parentTags.length === 0) {
      return;
    }
    const parentTagIds = parentTags.map((tag) => tag.id);
    showTagsInWorkspace(parentTagIds);
    parentTags.map((tag) => onAddTagToMap(tag));
  };

  const showTagsInWorkspace = (tagIds: Array<number>) => {
    setDraftWorkspace((prevWorkspace) => {
      const tagIdsToShow = tagIds.filter(
        (tagId) => !prevWorkspace.teammateWorkspaceTags.map((tag) => tag.id).includes(tagId),
      );
      tagIdsToShow.forEach((tagId) => {
        trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
          action: WorkspaceDisplayActionsEnum.ADD,
          section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
        });
      });
      return {
        ...prevWorkspace,
        teammateWorkspaceTags: prevWorkspace.teammateWorkspaceTags.concat(
          tagIdsToShow.map((tagId) => ({id: tagId, isSelected: true, isRecentlyViewed: undefined})),
        ),
      };
    });
  };

  // For toggling a tag in a search modal.
  const onToggleDropdownTag = (
    searchTag: SearchTagModel,
    parentTagIdsToAdd: Array<number>,
    childTagIdsToRemove: Array<number>,
  ) => {
    const tagId = searchTag[0].id;
    setDraftWorkspace((prevWorkspace) => {
      const isHidingTag = prevWorkspace.teammateWorkspaceTags.map((t) => t.id).includes(tagId);
      // Trigger tracking events for each tag we add to P1 via the search modal.
      if (!isHidingTag) {
        [tagId, ...parentTagIdsToAdd].forEach((id) => {
          trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
            action: WorkspaceDisplayActionsEnum.ADD,
            section: WorkspaceDisplaySectionTrackingTypesEnum.TAGS,
          });
        });
      }

      return {
        ...prevWorkspace,
        teammateWorkspaceTags: isHidingTag
          ? prevWorkspace.teammateWorkspaceTags.filter(
              (t) => t.id !== tagId && !childTagIdsToRemove.includes(t.id),
            )
          : _(prevWorkspace.teammateWorkspaceTags)
              .concat({id: tagId, isSelected: true, isRecentlyViewed: undefined})
              .concat(
                parentTagIdsToAdd.map((tId) => ({id: tId, isSelected: true, isRecentlyViewed: undefined})),
              )
              .value(),
      };
    });
    // add the tree of tags to the map so we can accurately find parent/child tag relationships
    searchTag.map((tag) => onAddTagToMap(tag));
  };

  const onAddTagToMap = (tag: TagModel) => {
    setTagModelMap((prevMap) => {
      if (tag.id in prevMap) {
        return prevMap;
      }
      return {
        ...prevMap,
        [tag.id]: tag,
      };
    });
  };

  const onToggleExpandItem = useCallback(
    ({key, defaultExpandedState}: ToggleExpandItemArgs) => {
      setCollapsedItems((prevItems) => computeExpansionToggledItems(prevItems, key, defaultExpandedState));
    },
    [setCollapsedItems],
  );

  const onToggleExpandTag = useCallback(
    ({tagId, defaultExpandedState}: ToggleExpandTagArgs) => {
      const key = buildCustomViewExpandedKey(tagId);
      onToggleExpandItem({key, defaultExpandedState});
    },
    [onToggleExpandItem],
  );

  const onDismissEditor = () => {
    if (didBlockNavigation) {
      unblockNavigation();
    }

    setCollapsedItems({});

    dispatch(stoppedEditingWorkspacesAction(panelId));
  };

  const onDismissDialog = () => {
    setVisibleDialog(null);
    cancelNavigation();
  };

  const onRequestCancelChanges = () => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_CANCEL);

    if (hasAnyChanges) {
      showDiscardChangesDialog();
    } else {
      onDismissEditor();
    }
  };

  const onRequestSaveChanges = () => {
    if (!hasAnyChanges) {
      const error = new Error(
        'WorkspacesEditorProvider cannot show Confirm Changes dialog without any pending changes',
      );
      reportToBugsnag(error);
    }
    onSaveChanges();
  };

  const onSaveChanges = async () => {
    if (!hasAnyChanges) {
      const error = new Error('WorkspacesEditorProvider cannot save without any pending changes');
      reportToBugsnag(error);
      return;
    }

    // The workspace could have changed (e.g. been updated outside of edit mode) while it was being edited.
    // For now, we simply don't allow saving the edits.
    const hasWorkspaceBeenUpdatedWhileEditing = !isSameVisualWorkspace(initialWorkspace, visualWorkspace);
    if (hasWorkspaceBeenUpdatedWhileEditing) {
      showConflictDialog();
      return;
    }

    try {
      setIsSavingWorkspaces(true);
      // We want each overrideAction to happen in order and synchronously.
      for (const overrideAction of parentTagOverrides) {
        const {newParentTagId, childTagId} = overrideAction;
        if (newParentTagId === ParentTagOverridesEnum.NO_CHANGE) {
          continue;
        }

        await dispatch(
          updateParentTagAction({
            childTagId: Number(childTagId),
            newParentTagId,
            additionalTagsMap: tagModelMap,
          }),
        );
      }

      const hasChangedWorkspace = !isSameVisualWorkspace(initialWorkspace, draftWorkspace);
      if (hasChangedWorkspace) {
        const selectedTeammates = _(draftWorkspace.teammates.items)
          .filter(({isSelected}) => isSelected)
          .map(({id}) => id)
          .value();

        // Update workspace-related preferences.
        await dispatch(
          updateWorkspacePreferencesAction({
            shouldAlphabetizeWorkspace: draftWorkspace.isAlphabetized,
            teammateIds: selectedTeammates,
          }),
        );

        // Update workspace resource.
        // TODO: Move from the updateWorkspaceAsyncAction PUT to a PATCH once that migration is complete.
        //  Currently, the patch will 404 if the user doesn't have an existing workspace. That will be fixed
        //  when we migrate all users to workspaces and we're guaranteed to have an existing workspace to patch.
        await dispatch(updateVisualWorkspaceAsyncAction(draftWorkspace));

        trackSaveSidebarEvent(initialWorkspace, draftWorkspace);
        setIsSavingWorkspaces(false);
        onDismissEditor();
      }
    } catch (err) {
      setIsSavingWorkspaces(false);
      showErrorDialog();
    }
  };

  return (
    <WorkspacesEditorContext.Provider
      value={{
        draftWorkspace,
        didBlockNavigation,
        visibleDialog: didBlockNavigation ? WorkspacesEditorDialogsEnum.DISCARD : visibleDialog,
        collapsedItems,
        isSaveDisabled: !hasAnyChanges,
        onToggleRootFolder,
        onToggleTag,
        onToggleTeammate,
        onToggleLens,
        onToggleDropdownLens,
        onCreateLens,
        onToggleExpandSection: onToggleExpandItem,
        onToggleExpandTag,
        onToggleTeamInbox,
        onToggleDropdownTeamInbox,
        onToggleDropdownTag,
        onToggleTeamCustomView,
        onToggleDropdownTeamCustomView,
        parentTagOverridesMap,
        onDropNestTag,
        onDropReorderNestedFolder,
        onDropReorderTag,
        onDropReorderTeamInbox,
        onDropReorderTeamView,
        onDropReorderTeammate,
        onDropReorderLens,
        onDismissDialog,
        onDismissEditor,
        onRequestCancelChanges,
        onRequestSaveChanges,
        onSaveChanges,
        shouldReloadTagResults,
        onReloadTagResults,
        onReloadTagResultsDone,
        tagModelMap,
        teamInboxModelMap,
        teamCustomViewModelMap,
        onToggleDropdownTeammate,
        teammateModelMap,
        lensModelMap,
        onTagCreated,
        isSavingWorkspaces,
      }}
    >
      {children}
    </WorkspacesEditorContext.Provider>
  );
};

/*
 * Helpers.
 */

function computeExpansionToggledItems(
  previousItems: CollapsedItemStates,
  toggledKey: CollapsedStateKey,
  defaultExpandedState: boolean,
) {
  const wasCollapsed = previousItems[toggledKey] ?? !defaultExpandedState;
  return {...previousItems, [toggledKey]: !wasCollapsed};
}
